// eslint-disable-next-line import/no-unresolved
import * as XLSX from "xlsx";
import type { UploadedFileData } from "../types";

/**
 * Parses a CSV or XLSX file and returns structured data.
 * XLSX files are converted to CSV client-side so the backend stays CSV-only.
 */
export async function parseFile(file: File): Promise<UploadedFileData> {
  const extension = file.name.split(".").pop()?.toLowerCase();

  if (extension === "xlsx" || extension === "xls") {
    return parseXLSX(file);
  }
  return parseCSV(file);
}

/**
 * Parse CSV: reads the full file as a Blob for upload, but only parses
 * a limited number of rows for the column mapping UI and preview.
 * Also counts total rows efficiently using streaming.
 */
async function parseCSV(file: File): Promise<UploadedFileData> {
  const MAX_SAMPLE_ROWS = 100;

  // Read only enough text for headers + sample rows using streaming
  const headers = await readCsvHeaders(file);
  if (headers.length === 0) throw new Error("File is empty");

  // Parse sample rows for column mapping and preview
  const sampleData = await readCsvSampleRows(file, MAX_SAMPLE_ROWS);

  // Count total rows using streaming (efficient for large files)
  const totalRows = await countCsvRows(file);

  // Keep the original file as-is for S3 upload (don't read full text into memory)
  const csvBlob = file.slice(0, file.size, "text/csv");

  return { columns: headers, data: sampleData, fileName: file.name, csvBlob, totalRows };
}

/**
 * Counts total data rows in a CSV file using streaming (excludes header).
 * Efficient for large files — doesn't load entire file into memory.
 */
async function countCsvRows(file: File): Promise<number> {
  const stream = file.stream();
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let lineCount = 0;
  let leftover = "";

  let done = false;
  while (!done) {
    const result = await reader.read();
    done = result.done;
    if (done) break;
    const value = result.value;
    const chunk = leftover + decoder.decode(value, { stream: true });
    const lines = chunk.split("\n");
    // Last element might be incomplete — carry it over
    leftover = lines.pop() || "";
    lineCount += lines.filter((l) => l.trim()).length;
  }
  // Count the last leftover if non-empty
  if (leftover.trim()) lineCount++;

  // Subtract 1 for the header row
  return Math.max(0, lineCount - 1);
}

async function readCsvHeaders(file: File): Promise<string[]> {
  // Read just the first chunk to get headers
  const chunk = file.slice(0, Math.min(file.size, 8192));
  const text = await chunk.text();
  const firstLine = text.split("\n")[0];
  if (!firstLine?.trim()) return [];
  // Strip trailing asterisks from headers (used as "required" markers in templates)
  return parseCSVLine(firstLine).map((h) => h.replace(/\*+$/, "").trim());
}

async function readCsvSampleRows(file: File, maxRows: number): Promise<Record<string, string>[]> {
  // Read enough of the file to get headers + maxRows
  // Estimate ~500 bytes per row as a reasonable upper bound
  const estimatedSize = Math.min(file.size, (maxRows + 2) * 500);
  const chunk = file.slice(0, estimatedSize);
  const text = await chunk.text();
  const lines = text.split("\n").filter((l) => l.trim());
  if (lines.length === 0) return [];

  // Strip trailing asterisks from headers (matching readCsvHeaders)
  const headers = parseCSVLine(lines[0]).map((h) => h.replace(/\*+$/, "").trim());
  const data: Record<string, string>[] = [];

  for (let i = 1; i < Math.min(lines.length, maxRows + 1); i++) {
    const values = parseCSVLine(lines[i]);
    const row: Record<string, string> = {};
    headers.forEach((h, idx) => {
      row[h] = values[idx] || "";
    });
    data.push(row);
  }

  return data;
}

async function parseXLSX(file: File): Promise<UploadedFileData> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });

  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) throw new Error("No sheets found in file");

  const sheet = workbook.Sheets[firstSheetName];
  const csvString = XLSX.utils.sheet_to_csv(sheet);
  const jsonData = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: "" });

  const columns = jsonData.length > 0 ? Object.keys(jsonData[0]) : [];
  const data: Record<string, string>[] = jsonData.map((row) => {
    const stringRow: Record<string, string> = {};
    columns.forEach((col) => {
      stringRow[col] = String(row[col] ?? "");
    });
    return stringRow;
  });

  const csvBlob = new Blob([csvString], { type: "text/csv" });

  return { columns, data, fileName: file.name, csvBlob };
}

/**
 * Computes preview values (shortest or longest) for each column.
 */
export function computePreviewValues(
  data: Record<string, string>[],
  mode: "shortest" | "longest"
): Record<string, string> {
  if (data.length === 0) return {};

  const columns = Object.keys(data[0]);
  const result: Record<string, string> = {};

  columns.forEach((col) => {
    let best = data[0][col] || "";
    for (let i = 1; i < data.length; i++) {
      const val = data[i][col] || "";
      if (mode === "shortest" && val.length < best.length) {
        best = val;
      } else if (mode === "longest" && val.length > best.length) {
        best = val;
      }
    }
    result[col] = best;
  });

  return result;
}

/**
 * Finds the real shortest and longest rows from a CSV file using streaming.
 * Scores rows by: full_name length (primary) + sum of all field lengths (secondary).
 * Returns the top 2 actual rows for each extreme from the CSV — not frankensteined combinations.
 * Memory-efficient: only keeps 4 rows in memory regardless of file size.
 *
 * @param source - A File object (local upload) or a signed URL string (S3 download)
 * @param knownHeaders - If provided, use these headers instead of parsing the first line
 */
export async function findExtremeRowsFromStream(
  source: File | string,
  knownHeaders?: string[]
): Promise<{
  shortest: Record<string, string>;
  longest: Record<string, string>;
  shortest2: Record<string, string>;
  longest2: Record<string, string>;
  /** Row indices (0-based, excluding header) of the extreme rows */
  shortestIdx: number;
  longestIdx: number;
  shortest2Idx: number;
  longest2Idx: number;
} | null> {
  const NAME_COLUMNS = ["first_name", "last_name", "firstname", "lastname", "nombre", "surname"];

  function scoreRow(row: Record<string, string>, headers: string[]): number {
    // Primary: full_name length (first_name + last_name)
    let nameLength = 0;
    for (const h of headers) {
      if (NAME_COLUMNS.includes(h.toLowerCase().replace(/\s+/g, "_"))) {
        nameLength += (row[h] || "").length;
      }
    }
    // Secondary: sum of all field lengths
    let totalLength = 0;
    for (const h of headers) {
      totalLength += (row[h] || "").length;
    }
    // Weight name heavily so it's the primary sort key
    return nameLength * 10000 + totalLength;
  }

  let reader: ReadableStreamDefaultReader<Uint8Array>;

  if (source instanceof File) {
    reader = source.stream().getReader();
  } else {
    // It's a URL — fetch it
    const res = await fetch(source);
    if (!res.ok || !res.body) return null;
    reader = res.body.getReader();
  }

  const decoder = new TextDecoder();
  let leftover = "";
  let headers: string[] | null = knownHeaders ? knownHeaders.map((h) => h.replace(/\*+$/, "").trim()) : null;
  // When knownHeaders provided, still need to skip the first CSV line (the header row)
  let headerLineSkipped = !knownHeaders; // false when knownHeaders provided (need to skip), true when parsing from file
  // Track top 2 shortest and top 2 longest with their row indices
  let shortestRow: Record<string, string> | null = null;
  let shortest2Row: Record<string, string> | null = null;
  let longestRow: Record<string, string> | null = null;
  let longest2Row: Record<string, string> | null = null;
  let shortestScore = Infinity;
  let shortest2Score = Infinity;
  let longestScore = -Infinity;
  let longest2Score = -Infinity;
  let shortestIdx = -1;
  let shortest2Idx = -1;
  let longestIdx = -1;
  let longest2Idx = -1;
  let rowsProcessed = 0;

  let done = false;
  while (!done) {
    const result = await reader.read();
    done = result.done;
    if (result.value) {
      leftover += decoder.decode(result.value, { stream: true });
    }

    const lines = leftover.split("\n");
    // Keep last incomplete line for next chunk
    leftover = lines.pop() || "";

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;

      if (!headers) {
        headers = parseCSVLine(trimmed).map((h) => h.replace(/\*+$/, "").trim());
        headerLineSkipped = true;
        continue;
      }

      // Skip the header row in the CSV when knownHeaders were provided
      if (!headerLineSkipped) {
        headerLineSkipped = true;
        continue;
      }

      const values = parseCSVLine(trimmed);
      const row: Record<string, string> = {};
      headers.forEach((h, idx) => {
        row[h] = values[idx] || "";
      });

      const score = scoreRow(row, headers);

      // Track top 2 shortest (lowest score)
      if (score < shortestScore) {
        shortest2Score = shortestScore;
        shortest2Row = shortestRow;
        shortest2Idx = shortestIdx;
        shortestScore = score;
        shortestRow = { ...row };
        shortestIdx = rowsProcessed;
      } else if (score < shortest2Score) {
        shortest2Score = score;
        shortest2Row = { ...row };
        shortest2Idx = rowsProcessed;
      }

      // Track top 2 longest (highest score)
      if (score > longestScore) {
        longest2Score = longestScore;
        longest2Row = longestRow;
        longest2Idx = longestIdx;
        longestScore = score;
        longestRow = { ...row };
        longestIdx = rowsProcessed;
      } else if (score > longest2Score) {
        longest2Score = score;
        longest2Row = { ...row };
        longest2Idx = rowsProcessed;
      }

      rowsProcessed++;
    }
  }

  // Process final leftover
  if (leftover.trim() && headers) {
    const values = parseCSVLine(leftover.trim());
    const row: Record<string, string> = {};
    headers.forEach((h, idx) => {
      row[h] = values[idx] || "";
    });
    const score = scoreRow(row, headers);
    if (score < shortestScore) {
      shortest2Score = shortestScore;
      shortest2Row = shortestRow;
      shortest2Idx = shortestIdx;
      shortestScore = score;
      shortestRow = { ...row };
      shortestIdx = rowsProcessed;
    } else if (score < shortest2Score) {
      shortest2Row = { ...row };
      shortest2Idx = rowsProcessed;
    }
    if (score > longestScore) {
      longest2Score = longestScore;
      longest2Row = longestRow;
      longest2Idx = longestIdx;
      longestScore = score;
      longestRow = { ...row };
      longestIdx = rowsProcessed;
    } else if (score > longest2Score) {
      longest2Row = { ...row };
      longest2Idx = rowsProcessed;
    }
  }

  if (!shortestRow || !longestRow) return null;

  return {
    shortest: shortestRow,
    longest: longestRow,
    shortest2: shortest2Row || shortestRow,
    longest2: longest2Row || longestRow,
    shortestIdx,
    longestIdx,
    shortest2Idx: shortest2Idx >= 0 ? shortest2Idx : shortestIdx,
    longest2Idx: longest2Idx >= 0 ? longest2Idx : longestIdx,
  };
}

/**
 * Extracts specific rows by their 0-based data indices from a CSV using streaming.
 * Efficient: stops reading as soon as all target rows are found.
 */
export async function getRowsByIndices(
  source: File | string,
  indices: number[],
  knownHeaders?: string[]
): Promise<Record<number, Record<string, string>>> {
  const targetSet = new Set(indices);
  const maxTarget = Math.max(...indices);
  const result: Record<number, Record<string, string>> = {};

  let reader: ReadableStreamDefaultReader<Uint8Array>;
  if (source instanceof File) {
    reader = source.stream().getReader();
  } else {
    const res = await fetch(source);
    if (!res.ok || !res.body) return result;
    reader = res.body.getReader();
  }

  const decoder = new TextDecoder();
  let leftover = "";
  let headers: string[] | null = knownHeaders ? knownHeaders.map((h) => h.replace(/\*+$/, "").trim()) : null;
  let headerLineSkipped = !knownHeaders;
  let rowIdx = 0;
  let done = false;

  while (!done) {
    const chunk = await reader.read();
    done = chunk.done;
    if (chunk.value) leftover += decoder.decode(chunk.value, { stream: true });

    const lines = leftover.split("\n");
    leftover = lines.pop() || "";

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      if (!headers) {
        headers = parseCSVLine(trimmed).map((h) => h.replace(/\*+$/, "").trim());
        headerLineSkipped = true;
        continue;
      }
      if (!headerLineSkipped) {
        headerLineSkipped = true;
        continue;
      }
      if (targetSet.has(rowIdx)) {
        const values = parseCSVLine(trimmed);
        const row: Record<string, string> = {};
        headers.forEach((h, i) => {
          row[h] = values[i] || "";
        });
        result[rowIdx] = row;
        if (Object.keys(result).length === targetSet.size) {
          reader.cancel();
          return result;
        }
      }
      rowIdx++;
      if (rowIdx > maxTarget && Object.keys(result).length === targetSet.size) {
        reader.cancel();
        return result;
      }
    }
  }

  // Process final leftover
  if (leftover.trim() && headers && targetSet.has(rowIdx)) {
    const values = parseCSVLine(leftover.trim());
    const row: Record<string, string> = {};
    headers.forEach((h, i) => {
      row[h] = values[i] || "";
    });
    result[rowIdx] = row;
  }

  return result;
}

/**
 * Finds extreme rows from a signed S3 URL using streaming.
 * Convenience wrapper for findExtremeRowsFromStream with a URL source.
 */
export async function findExtremeRowsFromUrl(
  signedUrl: string,
  headers?: string[]
): Promise<{ shortest: Record<string, string>; longest: Record<string, string> } | null> {
  return findExtremeRowsFromStream(signedUrl, headers);
}

/**
 * Generates a downloadable CSV template with default variable columns.
 */
export function downloadTemplate(): void {
  const headers = [
    "first_name*",
    "last_name*",
    "company",
    "address_1*",
    "address_2",
    "address_3",
    "city*",
    "state*",
    "zip*",
    "promo_code",
    "qr_link",
    "",
    "field*: required field",
  ];
  const sampleRows = [
    [
      "John",
      "Doe",
      "Acme Inc",
      "123 Main St",
      "",
      "",
      "New York",
      "NY",
      "10001",
      "SAVE10",
      "https://oppizi.com/promo/john123",
      "",
      "",
    ],
    [
      "Jane",
      "Smith",
      "Beta Corp",
      "456 Oak Ave",
      "",
      "",
      "Los Angeles",
      "CA",
      "90001",
      "DEAL15",
      "https://oppizi.com/promo/jane456",
      "",
      "",
    ],
  ];

  const csvContent = [headers.join(","), ...sampleRows.map((r) => r.join(","))].join("\n");
  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "variable-template.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Parses a single CSV line handling quoted values.
 */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ",") {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
  }
  result.push(current.trim());
  return result;
}
